/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/restrict-plus-operands */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { BaseComponent } from '../core/interfaces/BaseComponent';
import { BgVideo } from './components/ads-advanced-background-video/ads-advanced-background-video';
import { Component, Element, Prop, State, Watch, h } from '@stencil/core';
import { generateId } from '../core/utils/components';

/**
 * The AdvancedBackground component provides a flexible background with support for videos, images, colors, and gradients.
 * @tag `<ads-advanced-background>`
 * @slot - Content to show inside the background
 * @example
 * <ads-advanced-background background-type="Video" video-url-large='{"breakpoint": 1024, "videoUrl": "large-video.mp4"}'>
 *   <div>Content goes here</div>
 * </ads-advanced-background>
 */
@Component({
  tag: 'ads-advanced-background',
  styleUrl: 'ads-advanced-background.scss',
  shadow: true,
})
export class AdsAdvancedBackground implements BaseComponent {
  /**
   * @hidden
   */
  public component: string;

  constructor() {
    this.el.setAttribute('id', this.identifier || generateId('ads-advanced-background'));
    this.component = 'ads-advanced-background';
  }

  @Element() private el: HTMLAdsAdvancedBackgroundElement;

  /**
   * The unique identifier (optional)
   */
  @Prop({ attribute: 'id' }) identifier?: string;

  /**
   * The type of background (Color, Gradient, Image, Image Color, Image Gradient, Video, Video Image)
   */
  @Prop({ reflect: true }) backgroundType?: 'Color' | 'Gradient' | 'Image' | 'Image Color' | 'Image Gradient' | 'Video' | 'Video Image';

  /**
   * Enable background parallax
   */
  @Prop({ reflect: true }) enableParallax? = false;

  /**
   * Video URL for large screens
   */
  @Prop({ reflect: true }) videoUrlLarge?: string;

  /**
   * Video URL for medium screens
   */
  @Prop({ reflect: true }) videoUrlMedium?: string;

  /**
   * Video URL for small screens
   */
  @Prop({ reflect: true }) videoUrlSmall?: string;

  /**
   * Pause text for video controls
   */
  @Prop({ reflect: true }) pauseText?: string;

  /**
   * Play text for video controls
   */
  @Prop({ reflect: true }) playText?: string;

  /**
   * Enable video controls
   */
  @Prop({ reflect: true }) enableVideoControl? = false;

  /**
   * Enable video controls
   */
  @Prop({ reflect: true }) enableVideoControlText? = false;

  /**
   * Use inverted color for video controls
   */
  @Prop({ reflect: true }) useInvertedColorVideoControl? = false;

  /**
   * Custom color for video controls
   */
  @Prop({ reflect: true }) customColorVideoControl?: string;

  /**
   * Background image URL for large screens
   */
  @Prop({ reflect: true }) imageUrlLarge?: string;

  /**
   * Background image URL for medium screens
   */
  @Prop({ reflect: true }) imageUrlMedium?: string;

  /**
   * Background image URL for small screens
   */
  @Prop({ reflect: true }) imageUrlSmall?: string;

  /**
   * Custom height for the background
   */
  @Prop({ reflect: true }) customHeight?: string;

  @State() isPlaying: boolean = true;

  /**
   * Handle identifier property changes
   * @param {string} newValue The new value for "identifier"
   * @hidden
   */
  @Watch('identifier')
  handleIdentifierChange(newValue: string): void {
    this.el.setAttribute('id', newValue || generateId('ads-advanced-background'));
  }

  private parseImageUrl(imageUrl: string | undefined): string | undefined {
    if (imageUrl === undefined || imageUrl === '') {
      return undefined;
    }
    try {
      const parsed = JSON.parse(imageUrl);
      return parsed.imgUrl;
    } catch (e) {
      return undefined;
    }
  }

  private handlePlay = () => {
    this.isPlaying = true;
  };

  private handlePause = () => {
    this.isPlaying = false;
  };

  protected render(): HTMLElement {
    const width = window.innerWidth;
    let backgroundImage = '';
    const isVideo = this.backgroundType === 'Video';
    const isVideoImage = this.backgroundType === 'Video Image';
    const videoParallax = (isVideo || isVideoImage) && this.enableParallax;
    const isImageGradient = this.backgroundType === 'Image Gradient';

    if (this.imageUrlSmall && width <= 600) {
      backgroundImage = `url(${this.parseImageUrl(this.imageUrlSmall)})`;
    } else if (this.imageUrlMedium && width <= 1024) {
      backgroundImage = `url(${this.parseImageUrl(this.imageUrlMedium)})`;
    } else if (this.imageUrlLarge) {
      backgroundImage = `url(${this.parseImageUrl(this.imageUrlLarge)})`;
    }

    return (
      <div
        class={`advanced-background 
        ${this.enableParallax ? 'parallax' : ''} 
        ${videoParallax ? 'video-parallax' : ''}
        ${this.backgroundType?.includes('Gradient') ? 'gradient' : ''}
        ${isImageGradient ? 'image-gradient' : ''}
        ${isVideoImage ? 'video-image' : ''}`}
        style={{
          backgroundImage: !isImageGradient && !isVideoImage ? backgroundImage : undefined,
          height: videoParallax ? '100vh' : this.customHeight || 'auto',
        }}
      >
        {isImageGradient && (
          <div>
            <div class="gradient-overlay"></div>
            <div class="image-overlay" style={{ backgroundImage }}></div>
          </div>
        )}
        {isVideoImage && (
          <div class="video-image-container">
            <div class="background-image" style={{ backgroundImage }}></div>
            <BgVideo
              videoUrlLarge={this.videoUrlLarge}
              videoUrlMedium={this.videoUrlMedium}
              videoUrlSmall={this.videoUrlSmall}
              pauseText={this.pauseText}
              playText={this.playText}
              enableVideoControl={this.enableVideoControl}
              useInvertedColorVideoControl={this.useInvertedColorVideoControl}
              customColorVideoControl={this.customColorVideoControl}
              isPlaying={this.isPlaying}
              onPlay={this.handlePlay}
              onPause={this.handlePause}
              parallax={this.enableParallax}
              enableVideoControlText={this.enableVideoControlText}
            />
          </div>
        )}
        {isVideo && (
          <BgVideo
            videoUrlLarge={this.videoUrlLarge}
            videoUrlMedium={this.videoUrlMedium}
            videoUrlSmall={this.videoUrlSmall}
            pauseText={this.pauseText}
            playText={this.playText}
            enableVideoControl={this.enableVideoControl}
            useInvertedColorVideoControl={this.useInvertedColorVideoControl}
            customColorVideoControl={this.customColorVideoControl}
            poster={this.imageUrlLarge || this.imageUrlMedium || this.imageUrlSmall}
            isPlaying={this.isPlaying}
            onPlay={this.handlePlay}
            onPause={this.handlePause}
            parallax={this.enableParallax}
            enableVideoControlText={this.enableVideoControlText}
          />
        )}
        <div class={`content-container ${videoParallax ? 'parallax-content' : 'content-above-video'}`}>
          <slot></slot>
        </div>
      </div>
    );
  }
}
